/** /agents/:slug — governed, tabbed workspace for one enterprise AI agent. */
import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgentSummary, AgentWorkspace, Localized } from '@servicesai/api-client';
import {
  DataTable,
  EmptyState,
  GlassButton,
  GlassCard,
  Markdown,
  MetricCard,
  PageHeader,
  StatusPill,
  TypingDots,
  useI18n,
  type DataTableColumn,
} from '@servicesai/ui';
import { admin, api } from '../lib/api.js';
import { S } from '../strings.js';
import { HeroBand, MetricTile, SegmentedTabs } from '../components/elevated/kit.js';
import { AgentConfigPanel } from '../components/agents/AgentConfigPanel.js';
import { AgentNotesPanel } from '../components/agents/AgentNotesPanel.js';
import { AgentAutomationsPanel, AgentIntegrationsPanel } from '../components/agents/AgentResourcePanels.js';
import { AgentConfigurationAssistant } from '../components/agents/AgentConfigurationAssistant.js';
import { AgentReportingPanel } from '../components/agents/AgentReportingPanel.js';
import { AgentAuditPanel } from '../components/agents/AgentAuditPanel.js';
import { displayModel } from '../lib/aiBranding.js';
import {
  GovernanceTimelineCard,
  PromptDiffCard,
  TestRunCard,
  ThroughputCard,
} from '../components/agents/AgentWorkspaceExtras.js';
import { Icon } from '../components/elevated/icons.js';
import {
  AssuranceCommandCenter,
  isAssuranceWorkspace,
} from '../components/agents/AssuranceCommandCenter.js';
import {
  ComplaintsOperationsPanel,
  ComplaintsReportVisualization,
} from '../components/agents/ComplaintsOperationsPanel.js';
import { MysteryShopperOperationsPanel } from '../components/agents/MysteryShopperOperationsPanel.js';

const ACCENT: Record<string, string> = {
  gold: '#7c5e24',
  oasis: '#00608d',
  night: '#3e4046',
  sky: '#0173ab',
  sand: '#b2550a',
  rose: '#b52520',
};

type WorkspaceTab = 'overview' | 'operations' | 'activity' | 'prompt' | 'notes' | 'integrations' | 'automations' | 'reporting' | 'audit' | 'governance';
const WORKSPACE_TABS: WorkspaceTab[] = ['overview', 'activity', 'prompt', 'notes', 'integrations', 'automations', 'reporting', 'audit', 'governance'];

interface AgentDetail {
  agent: AgentSummary;
  runs: Record<string, unknown>[];
  report: {
    summary?: Localized;
    metric?: { label: Localized; value: string; unit?: Localized };
    data?: Record<string, unknown>;
  } | null;
  complaints?: Record<string, unknown>[];
}

interface RunRow {
  id: string;
  status: string;
  trigger: string;
  triggered_by?: string | null;
  model?: string | null;
  llm_grounded?: boolean | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  config_version_id?: string | null;
  prompt_hash?: string | null;
  schedule_id?: string | null;
  error?: string | null;
  started_at: string;
  finished_at?: string | null;
}

function AgentAvatar({
  slug,
  initials,
  color,
  size = 56,
}: {
  slug: string;
  initials: string;
  color: string;
  size?: number;
}) {
  const [failed, setFailed] = useState(false);
  const dim = { width: size, height: size };
  if (failed) {
    return (
      <span
        className="grid shrink-0 place-items-center rounded-full text-lg font-bold text-white shadow-inner ring-1 ring-aeblack-100"
        style={{ ...dim, backgroundColor: color }}
      >
        {initials}
      </span>
    );
  }
  return (
    <img
      src={`/agent-avatars/${slug}.png`}
      alt=""
      onError={() => setFailed(true)}
      style={dim}
      className="shrink-0 rounded-full object-cover shadow-sm ring-1 ring-aeblack-100"
    />
  );
}

export function AgentDetailPage() {
  const { slug = '' } = useParams();
  const { l, lang } = useI18n();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);
  const requestedTab = searchParams.get('tab');

  const detailQuery = useQuery({
    queryKey: ['agent-detail', slug],
    queryFn: () => admin().agent(slug) as Promise<AgentDetail>,
    enabled: Boolean(slug),
  });
  const workspaceQuery = useQuery({
    queryKey: ['agent-workspace', slug],
    queryFn: () => admin().agentWorkspace(slug),
    enabled: Boolean(slug),
  });
  const runMutation = useMutation({
    mutationFn: () => admin().runAgent(slug),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agent-detail', slug] }),
        queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] }),
        queryClient.invalidateQueries({ queryKey: ['agent-portfolio'] }),
      ]);
    },
  });

  if (detailQuery.isLoading || workspaceQuery.isLoading) {
    return (
      <div>
        <PageHeader title={l(S.agentWorkspace)} subtitle={l(S.agentWorkspaceLoading)} />
        <div className="h-96 animate-pulse rounded-xl border border-aeblack-100 bg-white" aria-busy="true" />
      </div>
    );
  }
  if (detailQuery.error || workspaceQuery.error || !detailQuery.data || !workspaceQuery.data) {
    const error = detailQuery.error ?? workspaceQuery.error;
    return <EmptyState title={l(S.loadFailed)} hint={(error as Error | null)?.message} />;
  }

  const detail = detailQuery.data;
  const workspace = workspaceQuery.data;
  const { agent } = detail;
  const hasOperations = agent.slug === 'complaints-manager' || agent.slug === 'mystery-shopper';
  const workspaceTabs: WorkspaceTab[] = hasOperations
    ? ['overview', 'operations', ...WORKSPACE_TABS.slice(1)]
    : WORKSPACE_TABS;
  const activeTab: WorkspaceTab = workspaceTabs.includes(requestedTab as WorkspaceTab)
    ? requestedTab as WorkspaceTab
    : 'overview';
  const draft = workspace.versions.find((version) => version.state === 'draft') ?? null;
  const setTab = (tab: WorkspaceTab) => {
    setSearchParams({ tab }, { replace: true });
  };
  const moveTab = (event: KeyboardEvent<HTMLButtonElement>, tab: WorkspaceTab) => {
    const current = workspaceTabs.indexOf(tab);
    const step = lang === 'ar' ? -1 : 1;
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? workspaceTabs.length - 1
          : event.key === 'ArrowRight'
            ? (current + step + workspaceTabs.length) % workspaceTabs.length
            : event.key === 'ArrowLeft'
              ? (current - step + workspaceTabs.length) % workspaceTabs.length
              : -1;
    if (nextIndex < 0) return;
    event.preventDefault();
    const nextTab = workspaceTabs[nextIndex]!;
    setTab(nextTab);
    window.requestAnimationFrame(() => document.getElementById(`agent-tab-${nextTab}`)?.focus());
  };
  const tabLabels: Record<WorkspaceTab, Localized> = {
    overview: S.agentTabOverview,
    operations: S.agentTabOperations,
    activity: S.agentTabActivity,
    prompt: S.agentTabPrompt,
    notes: S.agentTabNotes,
    integrations: S.agentTabIntegrations,
    automations: S.agentTabAutomations,
    reporting: S.agentTabReporting,
    audit: S.agentTabAudit,
    governance: S.agentTabGovernance,
  };

  const accent = ACCENT[agent.accent] ?? '#00608d';
  const heroSuccessRate = workspace.metrics.successRate;
  const heroDuration = workspace.metrics.averageDurationMs;
  const heroLatestRun = workspace.activity[0] as RunRow | undefined;

  return (
    <div dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <HeroBand
        accent={accent}
        eyebrow={l(S.agentWorkspace)}
        breadcrumbs={
          <>
            <Link to="/agents" className="transition hover:text-white">{l(S.agentsTitle)}</Link>
            <span className="mx-1.5" aria-hidden>›</span>
            <span className="font-mono">{agent.slug}</span>
          </>
        }
        title={
          <span className="flex items-center gap-4">
            <span className="relative shrink-0">
              <span
                aria-hidden
                className="absolute -inset-1.5 rounded-full opacity-80 blur-md"
                style={{ background: `conic-gradient(from 180deg, ${accent}, #cba344, ${accent})` }}
              />
              <AgentAvatar slug={agent.slug} initials={agent.initials} color={accent} size={64} />
            </span>
            <span>{l(agent.name)}</span>
          </span>
        }
        subtitle={
          <>
            <span className="font-semibold text-white/85">{l(agent.title)}</span>
            <span className="mt-1 block max-w-3xl text-white/65">{l(agent.blurb)}</span>
            <span className="mt-3 flex flex-wrap gap-1.5">
              {workspace.profile.labels.map((label) => (
                <span key={label} className="rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[10px] font-bold text-white/80">
                  {label}
                </span>
              ))}
              <span className="rounded-full border border-aegold-300/40 bg-aegold-500/20 px-2.5 py-1 font-mono text-[10px] font-bold text-aegold-200" dir="ltr">
                {agent.slug}
              </span>
            </span>
          </>
        }
        status={
          <>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wide ${workspace.effectiveConfig.enabled ? 'bg-aegreen-500/25 text-aegreen-100' : 'bg-aered-500/30 text-aered-100'}`}>
              {workspace.effectiveConfig.enabled ? `● ${l(S.enabled)}` : `● ${l(S.disabled)}`}
            </span>
            <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-white/75">
              {draft ? `${l(S.draft)} v${draft.version}` : `${l(S.published)} v${workspace.publishedVersion?.version ?? '—'}`}
            </span>
            {agent.kind === 'back_office' && agent.slug !== 'mystery-shopper' && (
              <button
                type="button"
                disabled={runMutation.isPending}
                onClick={() => runMutation.mutate()}
                className="inline-flex min-h-10 cursor-pointer items-center rounded-xl bg-aegold-500 px-4 text-xs font-extrabold text-aeblack-900 shadow-lg transition hover:brightness-110 disabled:opacity-50"
              >
                {runMutation.isPending ? l(S.running) : l(S.runNow)}
              </button>
            )}
          </>
        }
        metrics={
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricTile dark label={l(S.totalRuns)} value={workspace.metrics.runs} hint={l(S.lastHundredRuns)} accent={accent} />
            <MetricTile
              dark
              label={l(S.successRate)}
              value={heroSuccessRate == null ? '—' : `${heroSuccessRate}%`}
              hint={heroLatestRun ? `${l(S.lastActivity)} ${new Date(heroLatestRun.started_at).toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-GB')}` : l(S.neverRun)}
              accent={heroSuccessRate != null && heroSuccessRate >= 95 ? '#3f8e50' : accent}
            />
            <MetricTile
              dark
              label={l(S.averageDuration)}
              value={heroDuration == null ? '—' : heroDuration < 1000 ? `${heroDuration} ms` : `${(heroDuration / 1000).toFixed(1)} s`}
              accent={accent}
            />
            <MetricTile
              dark
              label={l(S.tokenUsage)}
              value={(workspace.metrics.promptTokens + workspace.metrics.completionTokens).toLocaleString('en-GB')}
              hint={`${workspace.metrics.promptTokens.toLocaleString('en-GB')} + ${workspace.metrics.completionTokens.toLocaleString('en-GB')}`}
              accent={accent}
            />
          </div>
        }
      />

      {runMutation.error && (
        <div className="aegov-alert alert-error mb-4" role="alert">
          <div className="alert-content">{l(S.actionFailed)}: {(runMutation.error as Error).message}</div>
        </div>
      )}

      <div className="mb-5 overflow-x-auto">
        <SegmentedTabs
          tabs={workspaceTabs.map((tab) => ({
            id: tab,
            label: l(tabLabels[tab]),
            badge: (
              <>
                {tab === 'notes' && workspace.notes.length > 0 && (
                  <span className={`ms-1.5 rounded-full px-1.5 py-0.5 text-[9px] ${activeTab === tab ? 'bg-white/20 text-white' : 'bg-aeblack-100 text-ink-soft'}`}>
                    {workspace.notes.length}
                  </span>
                )}
                {tab === 'prompt' && draft && (
                  <span className="ms-1.5 inline-block h-1.5 w-1.5 rounded-full bg-aegold-400" aria-label={l(S.draft)} />
                )}
              </>
            ),
          }))}
          active={activeTab}
          onChange={(id) => setTab(id as WorkspaceTab)}
          ariaLabel={l(S.agentWorkspaceSections)}
          idPrefix="agent-tab"
          onKeyMove={(event, id) => moveTab(event, id as WorkspaceTab)}
        />
      </div>

      <div className={activeTab === 'operations' ? 'block' : 'grid gap-4 2xl:grid-cols-[minmax(0,1fr)_24rem]'}>
        <section
          id={`agent-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`agent-tab-${activeTab}`}
          tabIndex={0}
          className="min-w-0 focus:outline-none"
        >
          {activeTab === 'overview' && <OverviewPanel detail={detail} workspace={workspace} />}
          {activeTab === 'operations' && agent.slug === 'complaints-manager' && <ComplaintsOperationsPanel />}
          {activeTab === 'operations' && agent.slug === 'mystery-shopper' && <MysteryShopperOperationsPanel workspace={workspace} />}
          {activeTab === 'activity' && <ActivityPanel workspace={workspace} />}
          {activeTab === 'prompt' && (
            <>
              <AgentConfigPanel slug={slug} workspace={workspace} />
              <PromptDiffCard workspace={workspace} />
            </>
          )}
          {activeTab === 'notes' && (
            <AgentNotesPanel
              slug={slug}
              selectedNoteIds={selectedNoteIds}
              onSelectedNoteIdsChange={setSelectedNoteIds}
            />
          )}
          {activeTab === 'integrations' && (
            <AgentIntegrationsPanel
              slug={slug}
              workspace={workspace}
              selectedNoteIds={selectedNoteIds}
            />
          )}
          {activeTab === 'automations' && (
            <AgentAutomationsPanel
              slug={slug}
              workspace={workspace}
              selectedNoteIds={selectedNoteIds}
            />
          )}
          {activeTab === 'reporting' && (
            <AgentReportingPanel
              slug={slug}
              workspace={workspace}
              selectedNoteIds={selectedNoteIds}
            />
          )}
          {activeTab === 'audit' && <AgentAuditPanel slug={slug} />}
          {activeTab === 'governance' && <GovernancePanel detail={detail} workspace={workspace} />}
        </section>
        {activeTab !== 'operations' && (
          <AgentConfigurationAssistant
            slug={slug}
            workspace={workspace}
            selectedNoteIds={selectedNoteIds}
          />
        )}
      </div>
    </div>
  );
}

function OverviewPanel({ detail, workspace }: { detail: AgentDetail; workspace: AgentWorkspace }) {
  const { l, lang } = useI18n();
  const { agent, report } = detail;
  const latestRun = workspace.activity[0] as RunRow | undefined;
  const successRate = workspace.metrics.successRate;
  const duration = workspace.metrics.averageDurationMs;
  const assuranceWorkspace = isAssuranceWorkspace(agent.slug, workspace.profile.labels);
  const accent = ACCENT[agent.accent] ?? '#00608d';
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={l(S.totalRuns)} value={workspace.metrics.runs} hint={l(S.lastHundredRuns)} />
        <MetricCard label={l(S.successRate)} value={successRate == null ? '—' : `${successRate}%`} tone={successRate == null ? 'neutral' : successRate >= 95 ? 'success' : successRate >= 80 ? 'warning' : 'danger'} />
        <MetricCard label={l(S.averageDuration)} value={duration == null ? '—' : duration < 1000 ? `${duration} ms` : `${(duration / 1000).toFixed(1)} s`} />
        <MetricCard label={l(S.tokenUsage)} value={(workspace.metrics.promptTokens + workspace.metrics.completionTokens).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')} hint={`${workspace.metrics.promptTokens.toLocaleString()} + ${workspace.metrics.completionTokens.toLocaleString()}`} />
      </div>

      <ThroughputCard workspace={workspace} accent={accent} />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
        <GlassCard className="p-5" strong>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-extrabold">{l(S.latestReport)}</h2>
              <p className="mt-1 text-xs text-ink-faint">{l(S.agentSpecificReportHint)}</p>
            </div>
            {latestRun && <StatusPill state={latestRun.status === 'succeeded' ? 'published' : latestRun.status === 'failed' ? 'rejected' : 'in_progress'} label={latestRun.status} />}
          </div>
          {assuranceWorkspace ? (
            <div className="mt-4">
              <AssuranceCommandCenter slug={agent.slug} />
            </div>
          ) : report ? (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap items-baseline justify-between gap-3 rounded-lg bg-aeblack-50 p-3">
                <p className="max-w-3xl text-sm text-ink-soft">{report.summary ? l(report.summary) : ''}</p>
                {report.metric && (
                  <div className="shrink-0 text-end">
                    <div className="font-mono text-2xl font-extrabold text-oasis">{report.metric.value}{report.metric.unit ? ` ${l(report.metric.unit)}` : ''}</div>
                    <div className="text-[11px] text-ink-faint">{l(report.metric.label)}</div>
                  </div>
                )}
              </div>
              <ReportBody slug={agent.slug} data={report.data ?? {}} />
              <AnalysisPanels data={report.data ?? {}} />
            </div>
          ) : (
            <EmptyState title={l(S.noRuns)} />
          )}
        </GlassCard>

        <div className="space-y-4">
          <GlassCard className="p-5">
            <h2 className="text-base font-extrabold">{l(S.healthProvenance)}</h2>
            <dl className="mt-4 space-y-3 text-xs">
              <ProvenanceRow label={l(S.runtimeState)} value={workspace.effectiveConfig.enabled ? l(S.enabled) : l(S.disabled)} />
              <ProvenanceRow label={l(S.model)} value={l(displayModel(workspace.effectiveConfig.runtime.model))} />
              <ProvenanceRow label={l(S.configurationVersion)} value={workspace.publishedVersion ? `v${workspace.publishedVersion.version}` : l(S.codeDefault)} />
              <ProvenanceRow label={l(S.promptHash)} value={workspace.publishedVersion?.prompt_hash?.slice(0, 16) ?? '—'} mono />
              <ProvenanceRow label={l(S.grounding)} value={workspace.effectiveConfig.guardrails.requireGrounding ? l(S.required) : l(S.optional)} />
              <ProvenanceRow label={l(S.lastActivity)} value={latestRun ? new Date(latestRun.started_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB') : l(S.neverRun)} />
            </dl>
          </GlassCard>
          <GlassCard className="p-5">
            <h2 className="text-base font-extrabold">{l(S.responsibilities)}</h2>
            <ul className="mt-3 space-y-2 text-sm text-ink-soft">
              {agent.responsibilities.map((responsibility, index) => (
                <li key={index} className="flex gap-2"><span aria-hidden className="text-gold-deep">•</span><span>{l(responsibility)}</span></li>
              ))}
            </ul>
          </GlassCard>
          {agent.kind === 'back_office' && <TestRunCard slug={agent.slug} />}
        </div>
      </div>
    </div>
  );
}

function ProvenanceRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-aeblack-50 pb-2 last:border-0 last:pb-0">
      <dt className="text-ink-faint">{label}</dt>
      <dd className={`max-w-[60%] break-all text-end font-bold ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

function ActivityPanel({ workspace }: { workspace: AgentWorkspace }) {
  const { l, lang } = useI18n();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const rows = workspace.activity as unknown as RunRow[];
  const columns: DataTableColumn<RunRow>[] = [
    {
      id: 'started',
      header: l(S.started),
      sortable: true,
      value: (row) => row.started_at,
      render: (row) => <time className="text-xs">{new Date(row.started_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}</time>,
    },
    {
      id: 'status',
      header: l(S.status),
      sortable: true,
      value: (row) => row.status,
      render: (row) => <StatusPill state={row.status === 'succeeded' ? 'published' : row.status === 'failed' ? 'rejected' : 'in_progress'} label={row.status} />,
    },
    {
      id: 'trigger',
      header: l(S.trigger),
      sortable: true,
      value: (row) => row.trigger,
      render: (row) => <div><div className="font-semibold">{row.trigger}</div><div className="text-[10px] text-ink-faint">{row.triggered_by ?? row.schedule_id ?? '—'}</div></div>,
    },
    {
      id: 'model',
      header: l(S.model),
      sortable: true,
      value: (row) => row.model ?? '',
      render: (row) => <span className="text-xs">{l(displayModel(row.model))}</span>,
    },
    {
      id: 'grounding',
      header: l(S.grounding),
      value: (row) => String(row.llm_grounded),
      render: (row) => row.llm_grounded == null ? '—' : <StatusPill state={row.llm_grounded ? 'published' : 'in_review'} label={row.llm_grounded ? l(S.grounded) : l(S.notGrounded)} />,
    },
    {
      id: 'tokens',
      header: l(S.tokens),
      value: (row) => Number(row.prompt_tokens ?? 0) + Number(row.completion_tokens ?? 0),
      sortable: true,
      render: (row) => <span className="font-mono text-xs">{Number(row.prompt_tokens ?? 0) + Number(row.completion_tokens ?? 0)}</span>,
    },
    {
      id: 'provenance',
      header: l(S.provenance),
      value: (row) => row.config_version_id,
      render: (row) => <div className="max-w-40"><code className="block truncate text-[10px]" title={row.config_version_id ?? ''}>{row.config_version_id?.slice(0, 12) ?? '—'}</code><code className="block truncate text-[9px] text-ink-faint" title={row.prompt_hash ?? ''}>{row.prompt_hash?.slice(0, 12) ?? '—'}</code></div>,
    },
    {
      id: 'duration',
      header: l(S.duration),
      value: (row) => row.finished_at ? new Date(row.finished_at).getTime() - new Date(row.started_at).getTime() : null,
      sortable: true,
      render: (row) => {
        const value = row.finished_at ? new Date(row.finished_at).getTime() - new Date(row.started_at).getTime() : null;
        return <span className="font-mono text-xs">{value == null ? '—' : `${(value / 1000).toFixed(1)}s`}</span>;
      },
    },
    {
      id: 'open',
      header: '',
      render: () => (
        <span className="block text-end" aria-hidden>
          <Icon
            name="arrow-end"
            size={14}
            className="inline-block text-aeblack-300 opacity-0 transition-opacity duration-200 [tr:hover_&]:opacity-100 rtl:-scale-x-100"
          />
        </span>
      ),
    },
  ];
  const selectedRun = rows.find((row) => row.id === selectedRunId) ?? null;
  return (
    <div className="space-y-4">
      <GlassCard className="p-5" strong>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><h2 className="text-lg font-extrabold">{l(S.activityHistory)}</h2><p className="mt-1 text-xs text-ink-faint">{l(S.activityHistoryHint)}</p></div>
          <StatusPill state="in_progress" label={`${rows.length} ${l(S.runs)}`} />
        </div>
      </GlassCard>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        emptyTitle={l(S.noRuns)}
        pageSize={20}
        filename="agent-activity.csv"
        onRowClick={(row) => setSelectedRunId(row.id)}
      />
      {workspace.scheduleActivity.length > 0 && (
        <GlassCard className="p-5">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-extrabold">{lang === 'ar' ? 'سجل الأتمتة' : 'Automation execution history'}</h3>
              <p className="mt-1 text-xs text-ink-faint">{lang === 'ar' ? 'عمليات التشغيل والإشعارات المجدولة مع نتائجها.' : 'Durable scheduled runs and notifications with outcomes.'}</p>
            </div>
            <StatusPill state="in_progress" label={String(workspace.scheduleActivity.length)} />
          </div>
          <div className="mt-4 space-y-2">
            {workspace.scheduleActivity.slice(0, 25).map((run) => (
              <div key={run.id} className="grid gap-2 rounded-lg border border-aeblack-100 bg-white p-3 text-xs md:grid-cols-[1fr_auto_auto]">
                <div>
                  <div className="font-bold">{run.name}</div>
                  <div className="mt-0.5 font-mono text-[10px] text-ink-faint">{run.purpose} · {run.schedule_id.slice(0, 12)}</div>
                  {run.error && <div className="mt-1 text-reject">{run.error}</div>}
                </div>
                <StatusPill state={run.status === 'succeeded' ? 'published' : run.status === 'failed' ? 'rejected' : run.status === 'suppressed' ? 'draft' : 'in_progress'} label={run.status} />
                <time className="text-ink-faint">{new Date(run.due_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}</time>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
      {rows.some((row) => row.error) && (
        <GlassCard className="p-5">
          <h3 className="text-base font-extrabold">{l(S.runErrors)}</h3>
          <div className="mt-3 space-y-2">
            {rows.filter((row) => row.error).map((row) => (
              <div key={row.id} className="rounded-lg border border-aered-100 bg-aered-50 p-3 text-xs">
                <div className="font-mono font-bold">{row.id}</div>
                <p className="mt-1 text-reject">{row.error}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
      {selectedRun && <RunDetailDrawer run={selectedRun} onClose={() => setSelectedRunId(null)} />}
    </div>
  );
}

/** Right-side drawer with full run provenance (plan point 53). Closes on X, Esc, or backdrop. */
function RunDetailDrawer({ run, onClose }: { run: RunRow; onClose: () => void }) {
  const { l, lang } = useI18n();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const locale = lang === 'ar' ? 'ar-AE' : 'en-GB';
  const promptTokens = Number(run.prompt_tokens ?? 0);
  const completionTokens = Number(run.completion_tokens ?? 0);
  const totalTokens = promptTokens + completionTokens;
  const durationMs = run.finished_at
    ? new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()
    : null;
  const triggerDetail = run.triggered_by ?? run.schedule_id ?? null;

  const copyHash = async () => {
    if (!run.prompt_hash) return;
    try {
      await navigator.clipboard.writeText(run.prompt_hash);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={l(S.runDetails)}>
      <button
        type="button"
        aria-label={l(S.closeRunDetails)}
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-aeblack-900/45 backdrop-blur-[2px]"
      />
      <aside className="absolute inset-y-0 right-0 flex h-full w-full max-w-md flex-col overflow-y-auto border-s border-aeblack-100 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-aeblack-100 px-5 py-4">
          <div>
            <h2 className="text-base font-extrabold">{l(S.runDetails)}</h2>
            <p className="mt-0.5 text-[11px] text-ink-faint">{l(S.runDetailsHint)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={l(S.closeRunDetails)}
            className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-lg text-ink-faint transition hover:bg-aeblack-50 hover:text-ink"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="flex-1 space-y-4 px-5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill
              state={run.status === 'succeeded' ? 'published' : run.status === 'failed' ? 'rejected' : 'in_progress'}
              label={run.status}
            />
            {run.llm_grounded != null && (
              <StatusPill
                state={run.llm_grounded ? 'published' : 'in_review'}
                label={run.llm_grounded ? l(S.grounded) : l(S.notGrounded)}
              />
            )}
          </div>

          <DrawerRow label={l(S.trigger)}>
            <div className="font-bold">{run.trigger}</div>
            {triggerDetail && (
              <div className="mt-0.5 break-all font-mono text-[10px] text-ink-faint" dir="ltr">{triggerDetail}</div>
            )}
          </DrawerRow>

          <DrawerRow label={l(S.model)}>
            <span className="text-xs font-bold">{l(displayModel(run.model))}</span>
          </DrawerRow>

          <DrawerRow label={l(S.tokens)}>
            <div className="flex items-baseline justify-between gap-2 font-mono text-xs font-bold" dir="ltr">
              <span>{promptTokens.toLocaleString('en-GB')} {l(S.promptTokens)}</span>
              <span>{completionTokens.toLocaleString('en-GB')} {l(S.completionTokens)}</span>
            </div>
            <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-aeblack-100" dir="ltr" aria-hidden>
              {totalTokens > 0 && (
                <span className="bg-seablue-600" style={{ width: `${(promptTokens / totalTokens) * 100}%` }} />
              )}
              <span className="flex-1 bg-aegold-400" />
            </div>
            <div className="mt-1 flex items-center gap-3 text-[10px] text-ink-faint">
              <span className="inline-flex items-center gap-1"><span aria-hidden className="h-1.5 w-1.5 rounded-full bg-seablue-600" />{l(S.promptTokens)}</span>
              <span className="inline-flex items-center gap-1"><span aria-hidden className="h-1.5 w-1.5 rounded-full bg-aegold-400" />{l(S.completionTokens)}</span>
              <span className="ms-auto font-mono font-bold" dir="ltr">{totalTokens.toLocaleString('en-GB')}</span>
            </div>
          </DrawerRow>

          <DrawerRow label={l(S.configVersionId)}>
            <code className="break-all font-mono text-xs font-bold" dir="ltr" title={run.config_version_id ?? ''}>
              {run.config_version_id ?? '—'}
            </code>
          </DrawerRow>

          <DrawerRow label={l(S.promptHash)}>
            {run.prompt_hash ? (
              <span className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate font-mono text-xs font-bold" dir="ltr" title={run.prompt_hash}>
                  {run.prompt_hash}
                </code>
                <GlassButton size="sm" variant="ghost" onClick={() => void copyHash()}>
                  <Icon name="copy" size={12} className="me-1 inline-block" />
                  {copied ? l(S.copied) : l(S.copy)}
                </GlassButton>
              </span>
            ) : (
              <span className="font-mono text-xs">—</span>
            )}
          </DrawerRow>

          <DrawerRow label={l(S.started)}>
            <time className="text-xs font-bold">{new Date(run.started_at).toLocaleString(locale)}</time>
          </DrawerRow>
          <DrawerRow label={l(S.finished)}>
            <time className="text-xs font-bold">
              {run.finished_at ? new Date(run.finished_at).toLocaleString(locale) : '—'}
            </time>
          </DrawerRow>
          <DrawerRow label={l(S.duration)}>
            <span className="font-mono text-xs font-bold" dir="ltr">
              {durationMs == null ? '—' : durationMs < 1000 ? `${durationMs} ms` : `${(durationMs / 1000).toFixed(1)} s`}
            </span>
          </DrawerRow>

          {run.error && (
            <div>
              <div className="text-[10px] font-extrabold uppercase tracking-wide text-ink-faint">{l(S.errorLabel)}</div>
              <pre className="mt-1 whitespace-pre-wrap break-all rounded-lg border border-aered-100 bg-aered-50 p-3 font-mono text-[11px] text-reject">
                {run.error}
              </pre>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function DrawerRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-b border-aeblack-50 pb-3 last:border-0">
      <div className="mb-1 text-[10px] font-extrabold uppercase tracking-wide text-ink-faint">{label}</div>
      {children}
    </div>
  );
}

function GovernancePanel({ detail, workspace }: { detail: AgentDetail; workspace: AgentWorkspace }) {
  const { l, lang } = useI18n();
  const approvals = workspace.approvals as {
    id: string;
    state: string;
    risk_tier: string;
    maker_id: string;
    checker_id: string | null;
    created_at: string;
    expires_at: string;
    decision_note: string | null;
    subject_id: string;
  }[];
  const complaints = detail.complaints ?? [];
  const accent = ACCENT[detail.agent.accent] ?? '#00608d';
  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-2">
        <ConsoleAgentChat slug={detail.agent.slug} lang={lang} />
        <GlassCard className="p-5" strong>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><h2 className="text-base font-extrabold">{l(S.governanceSummary)}</h2><p className="mt-1 text-xs text-ink-faint">{l(S.governanceSummaryHint)}</p></div>
            <StatusPill state={workspace.effectiveConfig.guardrails.humanApprovalForActions ? 'in_review' : 'draft'} label={workspace.effectiveConfig.guardrails.humanApprovalForActions ? l(S.humanApproval) : l(S.noActionApproval)} />
          </div>
          <dl className="mt-4 grid gap-3 text-xs sm:grid-cols-2">
            <ProvenanceRow label={l(S.configVersions)} value={String(workspace.versions.length)} />
            <ProvenanceRow label={l(S.approvalRequests)} value={String(approvals.length)} />
            <ProvenanceRow label={l(S.notesContext)} value={String(workspace.notes.length)} />
            <ProvenanceRow label={l(S.guardrails)} value={String(Object.values(workspace.effectiveConfig.guardrails).filter((value) => value === true).length)} />
            <ProvenanceRow label={l(S.integrations)} value={String(workspace.effectiveConfig.integrations.length)} />
            <ProvenanceRow label={l(S.automations)} value={String(workspace.effectiveConfig.schedules.length)} />
          </dl>
        </GlassCard>
      </div>

      <GovernanceTimelineCard slug={detail.agent.slug} workspace={workspace} accent={accent} />

      <GlassCard className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><h2 className="text-base font-extrabold">{l(S.approvalsAudit)}</h2><p className="mt-1 text-xs text-ink-faint">{l(S.approvalsAuditHint)}</p></div>
          <Link to="/approvals" className="aegov-btn btn-outline btn-xs inline-flex min-h-10 items-center justify-center">{l(S.openApprovals)}</Link>
        </div>
        {approvals.length === 0 ? (
          <EmptyState title={l(S.noAgentApprovals)} />
        ) : (
          <div className="mt-4 space-y-2">
            {approvals.map((approval) => (
              <div key={approval.id} className="grid gap-2 rounded-lg border border-aeblack-100 bg-white p-3 text-xs md:grid-cols-[auto_1fr_auto]">
                <StatusPill state={approval.state === 'approved' ? 'published' : approval.state === 'rejected' ? 'rejected' : 'in_review'} label={approval.state} />
                <div className="min-w-0">
                  <div className="font-mono font-bold">{approval.subject_id}</div>
                  <div className="mt-0.5 text-ink-faint">{l(S.maker)}: {approval.maker_id} · {l(S.risk)}: {approval.risk_tier}</div>
                  {approval.decision_note && <p className="mt-1 text-ink-soft">{approval.decision_note}</p>}
                </div>
                <time className="text-ink-faint">{new Date(approval.created_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}</time>
              </div>
            ))}
          </div>
        )}
      </GlassCard>

      {detail.report && (
        <GlassCard className="p-5">
          <h2 className="text-base font-extrabold">{l(S.latestReport)}</h2>
          <div className="mt-3">
            <ReportBody slug={detail.agent.slug} data={detail.report.data ?? {}} />
          </div>
        </GlassCard>
      )}

      {detail.complaints !== undefined && (
        <GlassCard className="p-5">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-extrabold">{l(S.complaintsQueue)}</h2>
            <StatusPill state={complaints.length ? 'in_review' : 'published'} label={String(complaints.length)} />
          </div>
          {complaints.length === 0 ? (
            <EmptyState title={l(S.noComplaints)} />
          ) : (
            <div className="mt-3 space-y-2">
              {complaints.map((complaint) => (
                <div key={String(complaint.id)} className="rounded-xl bg-aeblack-50 p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <StatusPill state={String(complaint.status)} label={String(complaint.status)} />
                    <span className="text-xs text-ink-faint">{String(complaint.severity)} · {String(complaint.category ?? '—')}</span>
                  </div>
                  {complaint.body ? <div className="mt-1 text-ink-soft">{String(complaint.body)}</div> : null}
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      )}
    </div>
  );
}

/** Try-it chat retains the existing public agent endpoint; no configuration assistant is implied. */
function ConsoleAgentChat({ slug, lang }: { slug: string; lang: 'en' | 'ar' }) {
  const { l } = useI18n();
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: 'user' as const, content: text }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const response = await api.agentChat(slug, { messages: next, lang });
      setMessages((current) => [...current, { role: 'assistant', content: response.reply }]);
    } catch {
      setMessages((current) => [...current, { role: 'assistant', content: l(S.loadFailed) }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassCard className="p-5" strong>
      <div>
        <h2 className="text-base font-extrabold">{l(S.chatWithAgent)}</h2>
        <p className="mt-1 text-xs text-ink-faint">{l(S.safeTestHint)}</p>
      </div>
      <div ref={scrollRef} aria-live="polite" className="mt-3 max-h-80 min-h-48 space-y-2 overflow-y-auto rounded-xl bg-aeblack-50 p-3">
        {messages.length === 0 && <div className="py-12 text-center text-xs text-ink-faint">{l(S.askPlaceholder)}</div>}
        {messages.map((message, index) => (
          <div key={index} className={message.role === 'user' ? 'text-end' : ''}>
            <span className={`inline-block max-w-[85%] rounded-2xl px-3 py-2 text-sm ${message.role === 'user' ? 'bubble-citizen' : 'bubble-concierge'}`}>
              {message.role === 'user' ? message.content : <Markdown>{message.content}</Markdown>}
            </span>
          </div>
        ))}
        {busy && <div className="bubble-concierge w-fit rounded-2xl px-3 py-2"><TypingDots /></div>}
      </div>
      <form className="mt-2 flex items-center gap-2" onSubmit={(event) => { event.preventDefault(); void send(); }}>
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder={l(S.askPlaceholder)}
          aria-label={l(S.askPlaceholder)}
          className="glass w-full rounded-full px-4 py-2 text-sm focus:outline-2 focus:outline-gold/70"
        />
        <GlassButton variant="primary" type="submit" disabled={busy || input.trim() === ''}>{l(S.simSend)}</GlassButton>
      </form>
    </GlassCard>
  );
}

/** Agent-specific structured report rendering with a raw JSON fallback. */
function ReportBody({ slug, data }: { slug: string; data: Record<string, unknown> }) {
  const { l } = useI18n();
  if (slug === 'complaints-manager') {
    return <ComplaintsReportVisualization data={data} />;
  }
  if (slug === 'kpi-measurer' && Array.isArray(data.services)) {
    const services = data.services as { name: Localized; volume: number; onTimePct: number; errorPct: number; satisfactionPct: number | null }[];
    return <ReportTable head={['Service', 'Volume', 'On-time %', 'Error %', 'CSAT %']} rows={services.map((service) => [l(service.name), String(service.volume), String(service.onTimePct), String(service.errorPct), service.satisfactionPct == null ? '—' : String(service.satisfactionPct)])} />;
  }
  if (slug === 'bureaucracy-radar' && Array.isArray(data.ranked)) {
    const ranked = data.ranked as { name: Localized; index: number; fields: number; manualDocuments: number; stages: number; decisionDays: number }[];
    return <ReportTable head={['Service', 'Friction', 'Fields', 'Docs', 'Stages', 'Days']} rows={ranked.map((row) => [l(row.name), String(row.index), String(row.fields), String(row.manualDocuments), String(row.stages), String(row.decisionDays)])} />;
  }
  if (slug === 'service-developer' && Array.isArray(data.recommendations)) {
    const recommendations = data.recommendations as { service: Localized; action: Localized; impact: string }[];
    return (
      <ul className="space-y-2 text-sm">
        {recommendations.map((recommendation, index) => (
          <li key={index} className="rounded-xl bg-aeblack-50 p-3">
            <div className="font-semibold">{l(recommendation.service)}</div>
            <div className="text-ink-soft">{l(recommendation.action)}</div>
            <div className="mt-1 text-[11px] uppercase text-gold-deep">{recommendation.impact}</div>
          </li>
        ))}
      </ul>
    );
  }
  if (slug === 'mystery-shopper' && Array.isArray(data.probes)) {
    const probes = data.probes as { lang: string; channel: string; completed: boolean; finalState: string; turns: number; transcript: { role: string; text: string }[] }[];
    return (
      <div className="space-y-3">
        {probes.map((probe, index) => (
          <details key={index} className="rounded-xl bg-aeblack-50 p-3 text-sm">
            <summary className="cursor-pointer font-semibold">{probe.channel} · {probe.lang} — {probe.completed ? '✓' : '!'} {probe.finalState} ({probe.turns} turns)</summary>
            <div className="mt-2 space-y-1 text-xs">
              {probe.transcript.slice(0, 40).map((turn, turnIndex) => <div key={turnIndex} className={turn.role === 'shopper' ? 'text-ink-faint' : 'text-ink-soft'}><b>{turn.role}:</b> {turn.text}</div>)}
            </div>
          </details>
        ))}
        {Array.isArray(data.employeeEvaluation) && data.employeeEvaluation.length > 0 && (
          <ReportTable
            head={['Reviewer', 'Handled', 'Breaches', 'Returns', 'Avg hrs']}
            rows={(data.employeeEvaluation as { display_name: string | null; handled: number; breaches: number; returns: number; avg_hours: number | null }[]).map((employee) => [employee.display_name ?? '—', String(employee.handled), String(employee.breaches), String(employee.returns), employee.avg_hours == null ? '—' : String(employee.avg_hours)])}
          />
        )}
      </div>
    );
  }
  if (slug === 'service-marketer' && Array.isArray(data.campaigns)) {
    const campaigns = data.campaigns as { segment: Localized; whatsappTemplate: Localized; sms: Localized; posterHtml: string }[];
    return (
      <div className="space-y-4">
        {campaigns.map((campaign, index) => (
          <div key={index} className="grid gap-3 sm:grid-cols-[1fr_240px]">
            <div className="text-sm"><div className="font-semibold">{l(campaign.segment)}</div><div className="mt-1 text-ink-soft">{l(campaign.whatsappTemplate)}</div><div className="mt-1 text-ink-soft">{l(campaign.sms)}</div></div>
            <iframe title={`poster-${index}`} srcDoc={campaign.posterHtml} sandbox="" referrerPolicy="no-referrer" className="h-64 w-full rounded-xl border border-aeblack-100" />
          </div>
        ))}
      </div>
    );
  }
  if (slug === 'voice-of-customer' && data.overall) {
    const overall = data.overall as { responses: number; average: number; satisfactionPct: number; deltaVsBaseline: number };
    const perService = (data.perService as { service_slug: string; n: number; satisfactionPct: number }[]) ?? [];
    return (
      <div className="space-y-3">
        <div className="text-sm text-ink-soft">{overall.responses} responses · avg {overall.average}/5 · {overall.satisfactionPct}% satisfied ({overall.deltaVsBaseline >= 0 ? '+' : ''}{overall.deltaVsBaseline} vs baseline)</div>
        {perService.length > 0 && <ReportTable head={['Service', 'Responses', 'CSAT %']} rows={perService.map((service) => [service.service_slug, String(service.n), String(service.satisfactionPct)])} />}
      </div>
    );
  }
  if (slug === 'journey-analyst' && Array.isArray(data.funnel)) {
    const funnel = data.funnel as { state: string; count: number }[];
    return <ReportTable head={['State', 'Applications']} rows={funnel.map((item) => [item.state, String(item.count)])} />;
  }
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-ink-faint">{l(S.rawReport)}</summary>
      <pre className="mt-2 max-h-96 overflow-auto rounded-lg bg-aeblack-50 p-3" dir="ltr">{JSON.stringify(data, null, 2)}</pre>
    </details>
  );
}

function AnalysisPanels({ data }: { data: Record<string, unknown> }) {
  const { l, lang } = useI18n();
  const analysis = data.analysis as {
    insights?: Localized[];
    recommendations?: Localized[];
  } | undefined;
  const insights = analysis?.insights ?? [];
  const recommendations = analysis?.recommendations ?? [];
  if (insights.length === 0 && recommendations.length === 0) return null;
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <div className="rounded-lg border border-aeblack-100 bg-white p-4">
        <h3 className="text-sm font-extrabold">{lang === 'ar' ? 'الرؤى' : 'Insights'}</h3>
        <ul className="mt-2 space-y-2 text-xs text-ink-soft">
          {insights.map((insight, index) => <li key={index}>• {l(insight)}</li>)}
        </ul>
      </div>
      <div className="rounded-lg border border-aeblack-100 bg-white p-4">
        <h3 className="text-sm font-extrabold">{lang === 'ar' ? 'التوصيات' : 'Recommendations'}</h3>
        <ul className="mt-2 space-y-2 text-xs text-ink-soft">
          {recommendations.map((recommendation, index) => <li key={index}>• {l(recommendation)}</li>)}
        </ul>
      </div>
    </div>
  );
}

function ReportTable({ head, rows }: { head: string[]; rows: string[][] }) {
  const items = rows.map((cells, index) => ({ id: String(index), cells }));
  const columns: DataTableColumn<(typeof items)[number]>[] = head.map((header, index) => ({
    id: String(index),
    header,
    sortable: true,
    value: (row) => row.cells[index] ?? '',
    render: (row) => row.cells[index] ?? '—',
  }));
  return <DataTable rows={items} columns={columns} rowKey={(row) => row.id} pageSize={15} filename="agent-report.csv" />;
}
